'use client';

import { FC, useState, useEffect, useCallback } from 'react';

// @mui
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Container from '@mui/material/Container';
// routes
// import { PATH_DASHBOARD as paths } from 'src/routes/paths';
// _mock
// import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { useRouter } from 'next/navigation';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';

import { useTranslation } from 'src/app/i18n/client';

// components
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';

//
import AccountGeneral from '../account-general';
import AccountBilling from '../account-billing';
import { MyESim } from '../../happy-gift/my-e-sim';
import AccountSocialLinks from '../account-social-links';
import { useAuthContext } from '../../../../../auth/hooks';
import AccountNotifications from '../account-notifications';
import AccountChangePassword from '../account-change-password';
import { useResponsive } from '../../../../../hooks/use-responsive';
import { FamilyCards } from '../../../../../components/family-cards';
import { fetchOneCustomer, getCustomerByPhoneOrMail } from '../../../../../api/customers';
import { ComponentTypeProps } from '../../../../../types/page-generator';
import { ICustomerItem } from '../../../../../types/customer-citypeople';
import { ActionTypes, useAppContext } from '../../../../../contexts/AppContext';
import {
  _userAbout,
  _userPlans,
  _userPayment,
  _userInvoices,
  _userAddressBook,
} from '../../../../../_mock';
import { MyApartments } from '../../../../../components/my-apartments';
import { getAttributes } from '../../../../../api/attributes';
import { TabLoan } from '../../../../wallet/components/tab-loan';

// ----------------------------------------------------------------------

export type AccountTab = {
  value: string;
  label: string;
  icon: JSX.Element;
};

const TABS: AccountTab[] = [
  {
    value: 'general',
    label: 'General',
    icon: <Iconify icon="solar:user-id-bold" width={24} />,
  },
  {
    value: 'myApartments',
    label: 'My apartments',
    icon: <Iconify icon="ph:building-apartment-fill" width={24} />,
  },
  {
    value: 'loanTab',
    label: 'Loan',
    icon: <Iconify icon="solar:wallet-outline" width={24} />,
  },
  {
    value: 'billing',
    label: 'Billing',
    icon: <Iconify icon="solar:bill-list-bold" width={24} />,
  },
  {
    value: 'notifications',
    label: 'Notifications',
    icon: <Iconify icon="solar:bell-bing-bold" width={24} />,
  },
  {
    value: 'social',
    label: 'Social links',
    icon: <Iconify icon="solar:share-bold" width={24} />,
  },
  {
    value: 'security',
    label: 'Security',
    icon: <Iconify icon="ic:round-vpn-key" width={24} />,
  },
  {
    value: 'family',
    label: 'Family',
    icon: <Iconify icon="icon-park-solid:family" width={24} />,
  },
  {
    value: 'eSim',
    label: 'eSim',
    icon: <Iconify icon="fluent:sim-20-filled" width={24} />,
  },
];

// ----------------------------------------------------------------------

const AccountView: FC<ComponentTypeProps> = ({ block, params }: ComponentTypeProps) => {
  const sectionType = params.section.sectionType;
  const { user } = useAuthContext();
  const { dispatch, state } = useAppContext();
  const [customer, setCustomer] = useState<ICustomerItem[] | any>([]);
  const [loading, setLoading] = useState(false);
  const settings = useSettingsContext();
  const { t } = useTranslation(params.params.lng);
  const [currentTab, setCurrentTab] = useState('general');
  const router = useRouter();
  const { logout } = useAuthContext();
  const isMobile = useResponsive('down', 'sm');

  const { attributes } = state;

  useEffect(() => {
    getAttributes(dispatch);
  }, [user]);

  const generalTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'generalTabItem')
      ? block.generalTabItem
      : true;
  const billingTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'billingTabItem')
      ? block.billingTabItem
      : true;
  const notificationsTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'notificationsTabItem')
      ? block.notificationsTabItem
      : true;
  const socialTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'socialTabItem')
      ? block.socialTabItem
      : true;
  const securityTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'securityTabItem')
      ? block.securityTabItem
      : true;
  const familyTabItem = Object.prototype.hasOwnProperty.call(block, 'familyTabItem')
    ? block?.familyTabItem
    : true;
  const eSimTabItem = Object.prototype.hasOwnProperty.call(block, 'eSimTabItem')
    ? block?.eSimTabItem
    : true;
  const myApartmentsTabItem = Object.prototype.hasOwnProperty.call(block, 'myApartmentsTabItem')
    ? block?.myApartmentsTabItem
    : true;
  const loanTabItem = Object.prototype.hasOwnProperty.call(block, 'loanTabItem')
    ? block?.loanTabItem
    : true;

  const activeTabNames: string[] = [];

  if (generalTabItem) activeTabNames.push('general');
  if (billingTabItem) activeTabNames.push('billing');
  if (notificationsTabItem) activeTabNames.push('notifications');
  if (socialTabItem) activeTabNames.push('social');
  if (securityTabItem) activeTabNames.push('security');
  if (familyTabItem) activeTabNames.push('family');
  if (eSimTabItem) activeTabNames.push('eSim');
  if (myApartmentsTabItem) activeTabNames.push('myApartments');
  if (loanTabItem) activeTabNames.push('loanTab');

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const logOut = () => {
    logout();
    dispatch({
      type: ActionTypes.SET_SMB_ACCOUNT,
      payload: {
        products: [],
        smbAccount: {},
        smbActivityId: '',
      },
    });
    dispatch({ type: ActionTypes.GET_VIRTUAL_CARD, payload: [] });
    localStorage.setItem('merchantId', '');
    router.push('/');
  };

  const init = () => {
    if (user) {
      getCustomerByPhoneOrMail(user.phone, user.email, dispatch).then((response: any) => {
        if (response) {
          setLoading(true);
          fetchOneCustomer(response[0]?.id).then((result) => {
            if (result) {
              setLoading(false);
              setCustomer(result.payload);
            }
          });
        }
      });
    }
  };

  useEffect(() => {
    init();
  }, [user]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <Stack direction="row">
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          sx={{
            mb: { xs: 3, md: 5 },
            '& .MuiTabs-scrollButtons': {
              width: { xs: '10px' },
              margin: { xs: '5px' },
            },
          }}
        >
          {TABS.filter((tab: AccountTab) => activeTabNames.includes(tab.value)).map(
            (tab: AccountTab) => (
              <Tab
                key={tab.value}
                label={`${t(`${tab.label}`)}`}
                icon={tab.icon}
                value={tab.value}
              />
            )
          )}
        </Tabs>
        {!isMobile && (
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={logOut}
            sx={{ mx: 2, mt: 1 }}
          >
            להתנתק
          </Button>
        )}
      </Stack>
      {isMobile && (
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={logOut}
          sx={{ mx: 2, mt: 1 }}
        >
          להתנתק
        </Button>
      )}

      {currentTab === 'general' && generalTabItem && (
        <AccountGeneral block={block} params={params} />
      )}

      {currentTab === 'billing' && billingTabItem && (
        <AccountBilling
          plans={_userPlans}
          cards={_userPayment}
          // @ts-ignore
          invoices={_userInvoices}
          // @ts-ignore
          addressBook={_userAddressBook}
        />
      )}

      {currentTab === 'notifications' && notificationsTabItem && <AccountNotifications />}

      {currentTab === 'social' && socialTabItem && (
        <AccountSocialLinks socialLinks={_userAbout.socialLinks} />
      )}

      {currentTab === 'security' && securityTabItem && <AccountChangePassword />}

      {currentTab === 'family' && familyTabItem && customer && (
        <FamilyCards
          block={block}
          customer={customer}
          loading={loading}
          refreshData={init}
          sectionType={sectionType}
        />
      )}

      {currentTab === 'eSim' && eSimTabItem && <MyESim block={block} />}

      {currentTab === 'myApartments' && familyTabItem && (
        <MyApartments block={block} attributes={attributes} />
      )}
      {currentTab === 'loanTab' && loanTabItem && <TabLoan />}
    </Container>
  );
};

export default AccountView;
